import React, { useCallback, useState, useContext } from 'react';
import { makeStyles } from '@mui/styles';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { TextField, Typography } from '@mui/material';
import { withStyles } from '@mui/styles';
import { Add, Business, Create, GroupAdd } from '@mui/icons-material';
import { postPatientList } from '../../lib/api/postPatientList';
import { AdminProgressBar } from '../AdminProgressBar';
import { ThemeContext } from '../../lib/contexts/ThemeContext';

const useStyles = makeStyles((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    margin: 'auto',
    width: 'fit-content',
    '& svg': {
      marginBottom: -6,
      marginRight: 4,
    },
    '& input': {
      borderColor: theme.palette.admin.main,
    },
  },
  formControl: {
    marginTop: theme.spacing(2),
    minWidth: 120,
  },
  formControlLabel: {
    marginTop: theme.spacing(3),
    marginLeft: 0,
    '& svg': {
      marginBottom: -6,
    },
  },
  addPatientListSmall: {
    backgroundColor: theme.palette.admin.main,
    color: theme.palette.admin.light,
    width: 180,
    height: 44,
    margin: '20px auto 0px',
    fontSize: 13,
    display: 'block',
    '&:hover': {
      backgroundColor: theme.palette.admin.dark,
      color: theme.palette.admin.light,
    },
    '& *': {
      color: theme.palette.admin.light,
      display: 'inline',
    },
  },
  addPatientList: {
    backgroundColor: theme.palette.admin.main,
    color: theme.palette.admin.light,
    width: 214,
    height: 50,
    marginTop: -25,
    marginRight: 25,
    display: 'block',
    '&:hover': {
      backgroundColor: theme.palette.admin.dark,
      color: theme.palette.admin.light,
    },
  },
  shareSwitch: {
    color: theme.palette.admin.main,
  },
  cancelButton: {
    color: theme.palette.error.main,
  },
  addButton: {
    color: theme.palette.admin.main,
  },
}));

const AdminGreenSwitch = withStyles((theme) => ({
  switchBase: {
    color: theme.palette.admin.dark,
    '&$checked': {
      color: theme.palette.admin.main,
    },
    '&$checked + $track': {
      backgroundColor: theme.palette.admin.main,
    },
  },
  checked: {},
  track: {},
}))(Switch);
const AdminGreenTextField = withStyles((theme) => ({
  root: {
    borderColor: theme.palette.admin.main,
    color: theme.palette.admin.main,
    '&:hover': {
      borderColor: theme.palette.admin.main,
      color: theme.palette.admin.main,
    },
  },
}))(TextField);

const AddPatientListDialog = ({ refetch, size, invisible }) => {
  const classes = useStyles();
  const {
    state: { darkMode },
  } = useContext(ThemeContext);
  const [open, setOpen] = useState(invisible);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [shareList, setShareList] = useState(true);

  const handleToggleOpen = useCallback(() => {
    setOpen(!open);
  }, [open]);

  const handleInputChange = useCallback((event) => {
    setName(event.target.value);
  }, []);

  const handleShareChange = useCallback((event) => {
    setShareList(event.target.checked);
  }, []);

  const handleSave = useCallback(() => {
    const cleanName = name.trim();
    if (cleanName.length > 3) {
      setSaving(true);
      postPatientList(cleanName, shareList).then(() => {
        setSaving(false);
        setOpen(false);
        setName('');
        setShareList(true);
        refetch();
      });
    }
  }, [name, shareList]);

  return (
    <>
      {!invisible && (
        <Button
          className={size == 'small' ? classes.addPatientListSmall : classes.addPatientList}
          variant={'contained'}
          onClick={handleToggleOpen}
          disabled={open || saving}
          startIcon={<Add style={{ color: darkMode ? '#000000' : '#ffffff' }} />}
        >
          {size == 'small' && <Typography>New List</Typography>}
        </Button>
      )}
      <Dialog
        maxWidth={'sm'}
        fullWidth={true}
        open={open}
        onClose={handleToggleOpen}
        aria-labelledby="add-patient-list-dialog-title"
        className={classes.form}
      >
        <DialogTitle id="add-patient-list-dialog-title">
          <GroupAdd style={{ color: darkMode ? '#000000' : '#ffffff' }} />
          {saving ? 'Saving' : 'Add a new'} Patient List
        </DialogTitle>
        <DialogContent>
          {saving ? (
            <AdminProgressBar style={{ margin: '50px auto 50px' }} />
          ) : (
            <>
              <DialogContentText>
                You may choose a nick name and whether to share with your organization.
              </DialogContentText>
              <form noValidate>
                <AdminGreenTextField
                  value={name}
                  onChange={handleInputChange}
                  variant={'outlined'}
                  fullWidth={true}
                  label={
                    <>
                      <Create style={{ color: darkMode ? '#000000' : '#ffffff' }} /> List Name
                    </>
                  }
                  placeholder={'e.g., Diabetes Coaching Cohort'}
                />

                <FormControlLabel
                  className={classes.formControlLabel}
                  control={
                    <AdminGreenSwitch
                      checked={shareList}
                      onChange={handleShareChange}
                    />
                  }
                  label={
                    <>
                      <Business style={{ color: darkMode ? '#000000' : '#ffffff' }} /> Share List with Organization
                    </>
                  }
                  labelPlacement={'start'}
                />
              </form>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleToggleOpen}
            className={classes.cancelButton}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            className={classes.addButton}
            disabled={saving}
          >
            Add List
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
export default AddPatientListDialog;
